import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Tabs, Tab, Typography, Avatar, Chip, Button,
  Card, CardContent, Stack, Divider, Alert, IconButton,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress,
} from '@mui/material';
import {
  Person, Note, Payment, History, Add, Delete,
  Edit, Block, MoreVert, CheckCircle, Pending, Cancel as CancelIcon,
  Badge, CloudUpload, Visibility,
} from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import api from '../../utils/api';
import DrawerShell from './_drawer/DrawerShell';
import DrawerHeader from './_drawer/DrawerHeader';
import useResponsiveAnchor from './_drawer/useResponsiveAnchor';
import { InfoSkeleton, NotesSkeleton, PaymentsSkeleton, HistorySkeleton } from './_drawer/DrawerSkeletons';
import StudentNoteDrawer from './StudentNoteDrawer';
import StudentPaymentDrawer from './StudentPaymentDrawer';
import StudentFormDrawer from './StudentFormDrawer';
import { RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

function formatDate(d) {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(d) {
  if (!d) return 'N/A';
  return new Date(d).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function getNoteTypeColor(type) {
  const map = { payment: 'info', behavior: 'warning', complaint: 'error', praise: 'success' };
  return map[type] || 'default';
}

function getPaymentStatusColor(status) {
  const map = { completed: 'success', pending: 'warning', failed: 'error', cancelled: 'error', refunded: 'info' };
  return map[status] || 'default';
}

const AADHAAR_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

function StudentDetailDrawer({ open, onClose, student, onChanged, showSnackbar, initialTab = 0 }) {
  const anchor = useResponsiveAnchor();
  const [tab, setTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  // Nested drawer state
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  // Overflow menu
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Aadhaar state
  const [hasAadhaar, setHasAadhaar] = useState(false);
  const [aadhaarUploading, setAadhaarUploading] = useState(false);
  const [aadhaarViewOpen, setAadhaarViewOpen] = useState(false);
  const [aadhaarBlobUrl, setAadhaarBlobUrl] = useState(null);
  const [aadhaarLoading, setAadhaarLoading] = useState(false);
  const aadhaarInputRef = useRef(null);
  const aadhaarBlobRef = useRef(null); // track current blob URL for cleanup

  const abortRef = useRef(null);

  // Revoke any outstanding blob URL when the drawer closes or student changes
  const revokeAadhaarBlob = useCallback(() => {
    if (aadhaarBlobRef.current) {
      URL.revokeObjectURL(aadhaarBlobRef.current);
      aadhaarBlobRef.current = null;
    }
    setAadhaarBlobUrl(null);
    setAadhaarViewOpen(false);
  }, []);

  useEffect(() => {
    if (!open || !student) return;
    setTab(initialTab);
    setHasAadhaar(Boolean(student.has_aadhaar));
    revokeAadhaarBlob();
    loadOverview();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, student?.id]);

  // Final cleanup on unmount
  useEffect(() => () => revokeAadhaarBlob(), [revokeAadhaarBlob]);

  const loadOverview = async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingOverview(true);
    setOverview(null);

    try {
      // Try the new combined endpoint first
      const res = await api.get(`/librarian/students/${student.id}/overview`, {
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setOverview(res.data.data);
      }
    } catch (err) {
      if (controller.signal.aborted) return;

      // Fallback: 4 individual calls if overview endpoint not yet deployed
      try {
        const [notesRes, paymentsRes, plansRes, historyRes] = await Promise.all([
          api.get(`/librarian/students/${student.id}/notes`, { signal: controller.signal }),
          api.get(`/librarian/students/${student.id}/payments`, { signal: controller.signal }),
          api.get('/librarian/plans', { signal: controller.signal }),
          api.get(`/librarian/students/${student.id}/history`, { signal: controller.signal }),
        ]);
        if (!controller.signal.aborted) {
          setOverview({
            notes: notesRes.data.data.notes,
            payments: paymentsRes.data.data.payments,
            plans: plansRes.data.data.plans,
            history: historyRes.data.data,
          });
        }
      } catch (fallbackErr) {
        if (!controller.signal.aborted) {
          showSnackbar('Failed to load student details', 'error');
        }
      }
    } finally {
      if (!controller.signal.aborted) setLoadingOverview(false);
    }
  };

  const handleBlock = async () => {
    setMenuAnchor(null);
    try {
      await api.put(`/librarian/students/${student.id}/block`, { blocked: !student.blocked });
      showSnackbar(student.blocked ? 'Student unblocked' : 'Student blocked', 'success');
      onChanged();
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    if (!window.confirm(`Delete student "${student.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/librarian/students/${student.id}`);
      showSnackbar('Student deleted', 'success');
      onChanged();
      onClose();
    } catch {
      showSnackbar('Failed to delete student', 'error');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/librarian/notes/${noteId}`);
      showSnackbar('Note deleted', 'success');
      // Splice locally for instant feedback
      setOverview((prev) => ({ ...prev, notes: prev.notes.filter((n) => n.id !== noteId) }));
    } catch {
      showSnackbar('Failed to delete note', 'error');
    }
  };

  const handleViewAadhaar = async () => {
    // Reuse already-fetched blob if the dialog is just being re-opened
    if (aadhaarBlobUrl) {
      setAadhaarViewOpen(true);
      return;
    }
    setAadhaarLoading(true);
    setAadhaarViewOpen(true);
    try {
      const res = await api.get(`/librarian/students/${student.id}/aadhaar`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      aadhaarBlobRef.current = url;
      setAadhaarBlobUrl(url);
    } catch (err) {
      setAadhaarViewOpen(false);
      const status = err.response?.status;
      if (status === 404) {
        showSnackbar('No Aadhaar card found for this student', 'warning');
      } else {
        showSnackbar(
          err.response?.data?.message || 'Failed to load Aadhaar card',
          'error',
        );
      }
    } finally {
      setAadhaarLoading(false);
    }
  };

  const handleCloseAadhaarDialog = () => {
    setAadhaarViewOpen(false);
    // Keep the blob cached in aadhaarBlobRef for the session — revokeAadhaarBlob()
    // runs on drawer close / student change to release memory at the right time.
  };

  const handleAadhaarFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset input so selecting the same file again still triggers onChange
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file (JPEG, PNG, etc.)', 'error');
      return;
    }
    if (file.size > AADHAAR_MAX_BYTES) {
      showSnackbar('File is too large — maximum size is 5 MB', 'error');
      return;
    }

    setAadhaarUploading(true);
    // Discard stale preview so the viewer shows the fresh upload
    revokeAadhaarBlob();
    try {
      const form = new FormData();
      form.append('aadhaar', file);
      const res = await api.post(
        `/librarian/students/${student.id}/aadhaar`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setHasAadhaar(res.data.data?.has_aadhaar ?? true);
      showSnackbar('Aadhaar card uploaded successfully', 'success');
      // Notify parent list so has_aadhaar stays consistent across the UI
      onChanged();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || 'Failed to upload Aadhaar card',
        'error',
      );
    } finally {
      setAadhaarUploading(false);
    }
  };

  const handleRemoveAadhaar = async () => {
    if (!window.confirm('Remove this student\'s Aadhaar card? This cannot be undone.')) return;
    try {
      await api.delete(`/librarian/students/${student.id}/aadhaar`);
      setHasAadhaar(false);
      revokeAadhaarBlob();
      showSnackbar('Aadhaar card removed', 'success');
      onChanged();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || 'Failed to remove Aadhaar card',
        'error',
      );
    }
  };

  const overflowMenu = (
    <>
      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More options">
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setEditDrawerOpen(true); }}>
          <Edit sx={{ mr: 1.5, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleBlock}>
          <Block sx={{ mr: 1.5, fontSize: 20 }} />
          {student?.blocked ? 'Unblock' : 'Block'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1.5, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );

  if (!student) return null;

  return (
    <>
      <DrawerShell open={open} onClose={onClose} anchor={anchor} ariaLabel="Student details">
        <DrawerHeader
          avatar={{
            children: student.name?.charAt(0).toUpperCase(),
            sx: { bgcolor: student.blocked ? 'error.main' : 'primary.main' },
          }}
          title={student.name}
          subtitle={student.email}
          onClose={onClose}
          overflowMenu={overflowMenu}
        />

        {/* Sticky Tabs */}
        <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
              },
            }}
          >
            <Tab icon={<Person fontSize="small" />} label="Info" iconPosition="start" />
            <Tab icon={<Note fontSize="small" />} label="Seat/Locker" iconPosition="start" />
            <Tab icon={<Payment fontSize="small" />} label="Payments" iconPosition="start" />
            <Tab icon={<History fontSize="small" />} label="History" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Scrollable tab content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>

          {/* TAB 0: Info */}
          {tab === 0 && (
            loadingOverview ? <InfoSkeleton /> : (
              <Box>
                <Card variant="outlined" sx={{ borderRadius: RADIUS.lg }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="700" gutterBottom>
                      Personal Information
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2" fontWeight="600">{student.phone || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Age</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {student.age ? `${student.age} years` : 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">City</Typography>
                        <Typography variant="body2" fontWeight="600">{student.city || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box>
                          <Chip
                            label={student.blocked ? 'Blocked' : 'Active'}
                            size="small"
                            color={student.blocked ? 'error' : 'success'}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="caption" color="text.secondary">Interested Courses</Typography>
                        <Typography variant="body2" fontWeight="600">
                          {student.interested_courses || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="caption" color="text.secondary">Member Since</Typography>
                        <Typography variant="body2" fontWeight="600">{formatDate(student.created_at)}</Typography>
                      </Box>
                    </Box>

                    {/* Aadhaar Card Section */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Badge fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight="700">
                        Aadhaar Card
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {hasAadhaar && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility fontSize="small" />}
                          onClick={handleViewAadhaar}
                          disabled={aadhaarUploading}
                          sx={{ borderRadius: RADIUS.md }}
                        >
                          View
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={hasAadhaar ? 'text' : 'outlined'}
                        startIcon={
                          aadhaarUploading
                            ? <CircularProgress size={14} />
                            : <CloudUpload fontSize="small" />
                        }
                        disabled={aadhaarUploading}
                        onClick={() => aadhaarInputRef.current?.click()}
                        sx={{ borderRadius: RADIUS.md }}
                      >
                        {aadhaarUploading
                          ? 'Uploading…'
                          : hasAadhaar ? 'Replace' : 'Upload'}
                      </Button>
                      {hasAadhaar && (
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          startIcon={<Delete fontSize="small" />}
                          disabled={aadhaarUploading}
                          onClick={handleRemoveAadhaar}
                          sx={{ borderRadius: RADIUS.md }}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                    {/* Hidden file input — accept images only */}
                    <input
                      ref={aadhaarInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      aria-label="Upload Aadhaar card image"
                      onChange={handleAadhaarFileChange}
                    />
                  </CardContent>
                </Card>
              </Box>
            )
          )}

          {/* TAB 1: Notes */}
          {tab === 1 && (
            loadingOverview ? <NotesSkeleton /> : (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setNoteDrawerOpen(true)}
                  fullWidth
                  sx={{ mb: 2, borderRadius: RADIUS.md }}
                >
                  Add Seat and locker no
                </Button>

                <AnimatePresence>
                  {!overview?.notes?.length ? (
                    <Alert severity="info">No notes yet for this student</Alert>
                  ) : (
                    overview.notes.map((note) => (
                      <MotionCard
                        key={note.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        variant="outlined"
                        sx={{
                          mb: 1.5,
                          borderRadius: RADIUS.lg,
                          borderLeft: note.is_important ? 4 : 0,
                          borderLeftColor: 'error.main',
                        }}
                      >
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              <Chip label={note.note_type} size="small" color={getNoteTypeColor(note.note_type)} />
                              {note.is_important && <Chip label="Important" size="small" color="error" />}
                              {note.seat_no && (
                                <Chip label={`Seat: ${note.seat_no}`} size="small" variant="outlined" color="primary" />
                              )}
                              {note.locker_no && (
                                <Chip label={`Locker: ${note.locker_no}`} size="small" variant="outlined" color="secondary" />
                              )}
                            </Box>
                            <IconButton size="small" onClick={() => handleDeleteNote(note.id)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>{note.note_text}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            By {note.librarian?.name} on {formatDateTime(note.created_at)}
                          </Typography>
                        </CardContent>
                      </MotionCard>
                    ))
                  )}
                </AnimatePresence>
              </Box>
            )
          )}

          {/* TAB 2: Payments */}
          {tab === 2 && (
            loadingOverview ? <PaymentsSkeleton /> : (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setPaymentDrawerOpen(true)}
                  fullWidth
                  sx={{ mb: 2, borderRadius: RADIUS.md }}
                >
                  Record Payment
                </Button>

                {!overview?.payments?.length ? (
                  <Alert severity="info">No payment records yet for this student</Alert>
                ) : (
                  overview.payments.map((payment) => (
                    <Card key={payment.id} variant="outlined" sx={{ mb: 1.5, borderRadius: RADIUS.lg }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="700" color="primary.main">
                            {formatCurrency(payment.amount_cents)}
                          </Typography>
                          <Chip
                            label={payment.status}
                            size="small"
                            color={getPaymentStatusColor(payment.status)}
                            icon={
                              payment.status === 'completed' ? <CheckCircle /> :
                              payment.status === 'pending' ? <Pending /> : <CancelIcon />
                            }
                          />
                        </Box>
                        <Stack spacing={0.5}>
                          {payment.plan && (
                            <Typography variant="body2" color="text.secondary">
                              Plan: <strong>{payment.plan.name}</strong> ({payment.plan.duration_days} days)
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            Method: <strong>{payment.payment_method}</strong>
                          </Typography>
                          {payment.transaction_id && (
                            <Typography variant="body2" color="text.secondary">
                              Txn: {payment.transaction_id}
                            </Typography>
                          )}
                          {payment.notes && (
                            <Typography variant="body2" color="text.secondary">{payment.notes}</Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(payment.created_at)}
                            {payment.recorder && ` · ${payment.recorder.name}`}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            )
          )}

          {/* TAB 3: History */}
          {tab === 3 && (
            loadingOverview ? <HistorySkeleton /> : (
              <Box>
                <Typography variant="subtitle2" fontWeight="700" gutterBottom>Bookings</Typography>
                {!overview?.history?.bookings?.length ? (
                  <Alert severity="info" sx={{ mb: 3 }}>No booking history</Alert>
                ) : (
                  <Box sx={{ mb: 3 }}>
                    {overview.history.bookings.map((booking) => (
                      <Card key={booking.id} variant="outlined" sx={{ mb: 1.5, borderRadius: RADIUS.lg }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="body2" fontWeight="700">{booking.library?.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{booking.library?.address}</Typography>
                              {booking.plan && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Plan: <strong>{booking.plan.name}</strong> — {formatCurrency(booking.plan.price_cents)}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatDate(booking.start_date)} – {formatDate(booking.end_date)}
                              </Typography>
                            </Box>
                            <Chip label={booking.status} size="small" color="primary" />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                <Typography variant="subtitle2" fontWeight="700" gutterBottom>Recent Sessions</Typography>
                {!overview?.history?.sessions?.length ? (
                  <Alert severity="info">No session history</Alert>
                ) : (
                  overview.history.sessions.map((dayGroup) => (
                    <Card key={dayGroup.date} variant="outlined" sx={{ mb: 2, borderRadius: RADIUS.lg }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight="700">
                            {new Date(dayGroup.date).toLocaleDateString('en-IN', {
                              weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                            })}
                          </Typography>
                          <Chip
                            label={`${Math.floor(dayGroup.totalDurationMinutes / 60)}h ${dayGroup.totalDurationMinutes % 60}m`}
                            color="primary"
                            size="small"
                          />
                        </Box>

                        {dayGroup.sessions.map((session, idx) => (
                          <Box
                            key={session.id}
                            sx={{
                              mb: idx < dayGroup.sessions.length - 1 ? 1.5 : 0,
                              pb: idx < dayGroup.sessions.length - 1 ? 1.5 : 0,
                              borderBottom: idx < dayGroup.sessions.length - 1 ? 1 : 0,
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="600">{session.library?.name}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Entry: {new Date(session.entered_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                              {session.exited_at && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Exit: {new Date(session.exited_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              )}
                            </Box>
                            {session.active ? (
                              <Chip label="Active" size="small" color="success" />
                            ) : (
                              <Chip
                                label={`${Math.floor(session.durationMinutes / 60)}h ${session.durationMinutes % 60}m`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            )
          )}
        </Box>
      </DrawerShell>

      {/* Aadhaar viewer dialog */}
      <Dialog
        open={aadhaarViewOpen}
        onClose={handleCloseAadhaarDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="aadhaar-dialog-title"
      >
        <DialogTitle id="aadhaar-dialog-title" sx={{ fontWeight: 700 }}>
          Aadhaar Card — {student?.name}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          {aadhaarLoading ? (
            <CircularProgress aria-label="Loading Aadhaar card" />
          ) : aadhaarBlobUrl ? (
            <Box
              component="img"
              src={aadhaarBlobUrl}
              alt={`Aadhaar card for ${student?.name}`}
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1, display: 'block' }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAadhaarDialog} sx={{ borderRadius: RADIUS.md }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nested drawers */}
      <StudentNoteDrawer
        open={noteDrawerOpen}
        onClose={() => setNoteDrawerOpen(false)}
        studentId={student?.id}
        onSaved={loadOverview}
        showSnackbar={showSnackbar}
      />

      <StudentPaymentDrawer
        open={paymentDrawerOpen}
        onClose={() => setPaymentDrawerOpen(false)}
        studentId={student?.id}
        plans={overview?.plans || []}
        onSaved={loadOverview}
        showSnackbar={showSnackbar}
      />

      <StudentFormDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        student={student}
        onSuccess={() => { setEditDrawerOpen(false); onChanged(); }}
        showSnackbar={showSnackbar}
      />
    </>
  );
}

export default StudentDetailDrawer;
